import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiEdit2, FiEye, FiTrash2 } from "react-icons/fi";
import Notification from "../../../components/common/notification/Notification";
import BillingCollectionToolbar from "../components/BillingCollectionToolbar";
import BillingConfirmDeleteModal from "../components/BillingConfirmDeleteModal";
import BillingDataTable from "../components/BillingDataTable";
import BillingEntityForm from "../components/BillingEntityForm";
import BillingFormModal from "../components/BillingFormModal";
import BillingPageHeader from "../components/BillingPageHeader";
import BillingStatusToggle from "../components/BillingStatusToggle";
import { useBillingCrudManager } from "../hooks/useBillingCrudManager";
import { createCatalogServicesService } from "../services/catalogServicesService";
import { createSubscriptionPlansService } from "../services/subscriptionPlansService";
import { useBillingApiClient } from "../services/useBillingApiClient";
import { formatCurrency } from "../utils/formatters";
import {
  getSubscriptionPlanFields,
  mapSubscriptionPlanToFormValues,
  mapSubscriptionPlanToPayload,
  subscriptionPlanInitialValues,
  subscriptionPlanValidationSchema,
} from "./subscriptionPlansConfig";
import "./SubscriptionPlansPage.css";

const BASE_PATH = "/subscription-plans";

const SubscriptionPlansPage = ({
  routeModalMode = null,
  routeModalId = null,
}) => {
  const navigate = useNavigate();
  const apiClient = useBillingApiClient();
  const subscriptionPlansService = useMemo(
    () => createSubscriptionPlansService(apiClient),
    [apiClient]
  );
  const catalogServicesService = useMemo(
    () => createCatalogServicesService(apiClient),
    [apiClient]
  );
  const [serviceOptions, setServiceOptions] = useState([]);
  const routeModalOpenedRef = useRef(false);

  const loadList = useCallback(
    ({ page, pageSize, filters }) => {
      if (filters?.serviceId && filters.serviceId !== "all") {
        return subscriptionPlansService.listByService(filters.serviceId, {
          page,
          pageSize,
        });
      }

      return subscriptionPlansService.list({ page, pageSize });
    },
    [subscriptionPlansService]
  );

  const loadEditableItem = useCallback(
    (itemOrId) => {
      const planId = typeof itemOrId === "object" ? itemOrId?.id : itemOrId;
      return subscriptionPlansService.getById(planId);
    },
    [subscriptionPlansService]
  );

  const manager = useBillingCrudManager({
    entityLabel: "Subscription plan",
    loadList,
    loadEditableItem,
    createItem: subscriptionPlansService.create,
    updateItem: subscriptionPlansService.update,
    deleteItem: subscriptionPlansService.remove,
    toggleItemStatus: subscriptionPlansService.toggleStatus,
    searchFields: [
      "plan_name",
      "plan_code",
      "billing_cycle",
      "offer_text",
      "service.name",
    ],
    defaultFilters: {
      serviceId: "all",
    },
    toFormValues: mapSubscriptionPlanToFormValues,
    toPayload: mapSubscriptionPlanToPayload,
  });
  const {
    closeForm,
    handleSubmit,
    openCreateForm,
    openEditForm,
  } = manager;

  useEffect(() => {
    let isMounted = true;

    const fetchServices = async () => {
      try {
        const response = await catalogServicesService.list();

        if (!isMounted) {
          return;
        }

        setServiceOptions(
          response.items.map((service) => ({
            value: String(service.id),
            label: service.name,
          }))
        );
      } catch (error) {
        if (isMounted) {
          setServiceOptions([]);
        }
      }
    };

    fetchServices();

    return () => {
      isMounted = false;
    };
  }, [catalogServicesService]);

  useEffect(() => {
    if (routeModalOpenedRef.current) {
      return;
    }

    if (routeModalMode === "create") {
      openCreateForm();
      routeModalOpenedRef.current = true;
      return;
    }

    if (routeModalMode === "edit" && routeModalId) {
      openEditForm(routeModalId);
      routeModalOpenedRef.current = true;
    }
  }, [openCreateForm, openEditForm, routeModalId, routeModalMode]);

  const handleCloseForm = () => {
    closeForm();

    if (routeModalMode) {
      navigate(BASE_PATH, { replace: true });
    }
  };

  const handleFormSubmit = async (values) => {
    const didSucceed = await handleSubmit(values);

    if (didSucceed && routeModalMode) {
      navigate(BASE_PATH, { replace: true });
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "S.No.",
        key: "serial",
        render: (_, index) => (manager.page - 1) * manager.pageSize + index + 1,
      },
      {
        header: "Plan",
        key: "plan_name",
        cellClassName: "subscription-plans-page__name-cell",
        render: (plan) => (
          <div className="subscription-plans-page__name-block">
            <span className="subscription-plans-page__name">{plan.plan_name}</span>
            <span className="subscription-plans-page__code">{plan.plan_code}</span>
          </div>
        ),
      },
      {
        header: "Price",
        key: "base_price",
        render: (plan) => (
          <div className="subscription-plans-page__price">
            {formatCurrency(plan.base_price)}
          </div>
        ),
      },
      {
        header: "Billing",
        key: "billing_cycle",
        render: (plan) => (
          <span className="subscription-plans-page__badge">
            {plan.billing_cycle || "N/A"}
          </span>
        ),
      },
      {
        header: "Users",
        key: "max_users",
        render: (plan) => `${plan.max_users ?? 0} users`,
      },
      {
        header: "Trial",
        key: "trial_days",
        render: (plan) => `${plan.trial_days ?? 0} days`,
      },
      {
        header: "Service",
        key: "service",
        render: (plan) =>
          plan.service?.name ? (
            <span className="subscription-plans-page__service-chip">
              {plan.service.name}
            </span>
          ) : (
            "Unassigned"
          ),
      },
      {
        header: "Offer",
        key: "offer_text",
        render: (plan) => (
          <span className="subscription-plans-page__offer">
            {plan.offer_text || "No offer text"}
          </span>
        ),
      },
    ],
    [manager.page, manager.pageSize]
  );

  return (
    <>
      <Notification />
      <div className="subscription-plans-page">
        <BillingPageHeader
          title="Subscription Plans"
          description="Manage subscription plans with reusable billing controls, service-based filtering, consistent status actions, and modal-based create or edit flows."
          actionLabel="Add Subscription Plan"
          onAction={openCreateForm}
        />

        <BillingCollectionToolbar
          searchValue={manager.searchValue}
          onSearchChange={manager.setSearchValue}
          searchPlaceholder="Search by plan name, code, billing cycle, service, or offer text"
          summary={`${manager.filteredRecords.length} plans on this page`}
        >
          <label htmlFor="subscription-plans-service-filter">
            Service Filter
            <select
              id="subscription-plans-service-filter"
              value={manager.filters.serviceId}
              onChange={(event) =>
                manager.updateFilters({
                  serviceId: event.target.value,
                })
              }
            >
              <option value="all">All services</option>
              {serviceOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </BillingCollectionToolbar>

        <BillingDataTable
          columns={columns}
          rows={manager.filteredRecords}
          keyExtractor={(plan) => plan.id}
          isLoading={manager.isLoading}
          errorMessage={manager.errorMessage}
          errorActionLabel="Retry"
          onErrorAction={manager.refresh}
          emptyTitle="No subscription plans found"
          emptyMessage="Try changing the service filter or create a new plan to get started."
          emptyActionLabel="Create Plan"
          onEmptyAction={manager.openCreateForm}
          currentPage={manager.page}
          totalPages={manager.totalPages}
          onPageChange={manager.setPage}
          renderRowActions={(plan) => (
            <div className="subscription-plans-page__actions">
              <div className="subscription-plans-page__action-icons">
                <Link
                  className="subscription-plans-page__icon-button subscription-plans-page__icon-button--ghost"
                  to={`${BASE_PATH}/${plan.id}`}
                  aria-label="View subscription plan"
                  title="View"
                >
                  <FiEye size={16} />
                </Link>
                <button
                  type="button"
                  className="subscription-plans-page__icon-button"
                  onClick={() => manager.openEditForm(plan.id)}
                  aria-label="Edit subscription plan"
                  title="Edit"
                >
                  <FiEdit2 size={16} />
                </button>
                <button
                  type="button"
                  className="subscription-plans-page__icon-button subscription-plans-page__icon-button--danger"
                  onClick={() => manager.askToDelete(plan)}
                  aria-label="Delete subscription plan"
                  title="Delete"
                >
                  <FiTrash2 size={16} />
                </button>
              </div>
              <BillingStatusToggle
                checked={Boolean(plan.isActive)}
                disabled={manager.isSubmitting}
                onChange={() =>
                  manager.handleStatusToggle(plan, !Boolean(plan.isActive))
                }
              />
            </div>
          )}
        />

        <BillingFormModal
          isOpen={manager.formState.isOpen}
          title={
            manager.formState.mode === "edit"
              ? "Edit Subscription Plan"
              : "Add Subscription Plan"
          }
          description="Use the reusable billing form to manage pricing, usage limits, service mapping, and promotional copy."
          onClose={handleCloseForm}
        >
          <BillingEntityForm
            initialValues={manager.formState.values || subscriptionPlanInitialValues}
            validationSchema={subscriptionPlanValidationSchema}
            fields={getSubscriptionPlanFields(serviceOptions)}
            onSubmit={handleFormSubmit}
            isSubmitting={manager.isSubmitting}
            submitLabel={
              manager.formState.mode === "edit" ? "Update Plan" : "Create Plan"
            }
            onCancel={handleCloseForm}
          />
        </BillingFormModal>

        <BillingConfirmDeleteModal
          isOpen={Boolean(manager.deleteTarget)}
          title="Delete subscription plan"
          description={`This will permanently remove ${
            manager.deleteTarget?.plan_name || "this plan"
          } from the billing catalogue.`}
          isBusy={manager.isSubmitting}
          onClose={manager.cancelDelete}
          onConfirm={manager.confirmDelete}
        />
      </div>
    </>
  );
};

export default SubscriptionPlansPage;
